import type { ChatAttachment, MessageReferenceChatType, MessageReferencePreview } from "./chatApi";

export interface RealtimeEvent {
  type: string;
  chat_id: string;
  message_id?: string;
  sequence?: number;
  sender_user_id?: string;
  body?: string;
  idempotency_key?: string;
  edit_version?: number;
  root_post_id?: string;
  reply_to?: MessageReferencePreview;
  forwarded_from?: MessageReferencePreview;
  attachments?: ChatAttachment[];
  created_at?: string;
  updated_at?: string;
  edited_at?: string;
  deleted_at?: string;
  deleted_by_user_id?: string;
  receipt_user_id?: string;
  delivered_seq?: number;
  read_seq?: number;
  conversation_type?: "group" | "channel";
  lifecycle_state?: "active" | "closed" | "removed";
  actor_user_id?: string;
  occurred_at?: string;
}

export interface RealtimeClientOptions {
  accessToken: string;
  gatewayOrigin?: string;
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
  onEvent: (event: RealtimeEvent) => void;
}

export interface RealtimeSubscription {
  close: () => void;
}

const REALTIME_RECONNECT_BASE_DELAY_MS = 1_000;
const REALTIME_RECONNECT_MAX_DELAY_MS = 10_000;
const LIFECYCLE_EVENT_FIELDS = new Set([
  "type",
  "chat_id",
  "conversation_type",
  "lifecycle_state",
  "actor_user_id",
  "occurred_at",
]);
const RECEIPT_EVENT_FIELDS = new Set([
  "type",
  "chat_id",
  "receipt_user_id",
  "delivered_seq",
  "read_seq",
  "created_at",
  "updated_at",
  "edited_at",
  "deleted_at",
  "occurred_at",
]);
const MESSAGE_EVENT_FIELDS = new Set([
  "type",
  "chat_id",
  "message_id",
  "sequence",
  "sender_user_id",
  "body",
  "idempotency_key",
  "edit_version",
  "root_post_id",
  "reply_to",
  "forwarded_from",
  "attachments",
  "created_at",
  "updated_at",
  "edited_at",
  "deleted_at",
  "deleted_by_user_id",
  "occurred_at",
]);
const MESSAGE_REFERENCE_PREVIEW_FIELDS = new Set([
  "chat_id",
  "chat_type",
  "message_id",
  "sequence",
  "sender_user_id",
  "body_preview",
  "has_attachments",
  "created_at",
]);
const ATTACHMENT_FIELDS = new Set(["media_id", "kind"]);

function toWebSocketOrigin(origin: string): string {
  if (origin.startsWith("https://")) {
    return `wss://${origin.slice("https://".length)}`;
  }
  if (origin.startsWith("http://")) {
    return `ws://${origin.slice("http://".length)}`;
  }
  if (origin.startsWith("wss://") || origin.startsWith("ws://")) {
    return origin;
  }
  return `ws://${origin}`;
}

function normalizeOrigin(input: string): string {
  const trimmed = input.trim();
  if (trimmed.endsWith("/")) {
    return trimmed.slice(0, -1);
  }
  return trimmed;
}

function resolveGatewayOrigin(override?: string): string {
  if (typeof override === "string" && override.trim() !== "") {
    return normalizeOrigin(override);
  }

  if (typeof import.meta.env.VITE_GATEWAY_ORIGIN === "string" && import.meta.env.VITE_GATEWAY_ORIGIN !== "") {
    return normalizeOrigin(import.meta.env.VITE_GATEWAY_ORIGIN);
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return normalizeOrigin(window.location.origin);
  }

  return "http://localhost:8080";
}

function resolveRealtimeURL(gatewayOrigin: string, accessToken: string): string {
  const baseURL = toWebSocketOrigin(gatewayOrigin);
  const url = new URL(`${baseURL}/v1/realtime/ws`);
  url.searchParams.set("access_token", accessToken);
  return url.toString();
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

function isLifecycleConversationType(value: unknown): value is "group" | "channel" {
  return value === "group" || value === "channel";
}

function isValidTimestamp(value: unknown): value is string {
  return isNonEmptyString(value) && !Number.isNaN(Date.parse(value));
}

function hasOnlyFields(event: Record<string, unknown>, fields: Set<string>): boolean {
  return Object.keys(event).every((key) => fields.has(key));
}

function hasOnlyLifecycleFields(event: Record<string, unknown>): boolean {
  return hasOnlyFields(event, LIFECYCLE_EVENT_FIELDS);
}

function isMessageReferenceChatType(value: unknown): value is MessageReferenceChatType {
  return value === "direct" || value === "group" || value === "channel";
}

function optionalTrimmedString(record: Record<string, unknown>, fieldName: string): string | null | undefined {
  if (!Object.prototype.hasOwnProperty.call(record, fieldName)) {
    return undefined;
  }
  const value = record[fieldName];
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim();
  return normalized === "" ? undefined : normalized;
}

function optionalNumber(record: Record<string, unknown>, fieldName: string): number | null | undefined {
  if (!Object.prototype.hasOwnProperty.call(record, fieldName)) {
    return undefined;
  }
  const value = record[fieldName];
  if (value === undefined) {
    return undefined;
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  return Math.trunc(value);
}

function normalizeRealtimeReference(value: unknown): MessageReferencePreview | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, unknown>;
  if (!hasOnlyFields(record, MESSAGE_REFERENCE_PREVIEW_FIELDS)) {
    return null;
  }

  const messageID = optionalTrimmedString(record, "message_id");
  const sequence = optionalNumber(record, "sequence");
  if (messageID === null || messageID === undefined || sequence === null || sequence === undefined || sequence <= 0) {
    return null;
  }

  const chatID = optionalTrimmedString(record, "chat_id");
  const senderUserID = optionalTrimmedString(record, "sender_user_id");
  const bodyPreview = optionalTrimmedString(record, "body_preview");
  const createdAt = optionalTrimmedString(record, "created_at");
  if (chatID === null || senderUserID === null || bodyPreview === null || createdAt === null) {
    return null;
  }

  const chatTypeValue = record.chat_type;
  let chatType: MessageReferenceChatType | undefined;
  if (chatTypeValue !== undefined) {
    if (typeof chatTypeValue !== "string") {
      return null;
    }
    const normalizedChatType = chatTypeValue.trim();
    if (normalizedChatType !== "") {
      if (!isMessageReferenceChatType(normalizedChatType)) {
        return null;
      }
      chatType = normalizedChatType;
    }
  }

  const hasAttachmentsValue = record.has_attachments;
  if (
    Object.prototype.hasOwnProperty.call(record, "has_attachments") &&
    typeof hasAttachmentsValue !== "boolean"
  ) {
    return null;
  }

  return {
    ...(chatID === undefined ? {} : { chat_id: chatID }),
    ...(chatType === undefined ? {} : { chat_type: chatType }),
    message_id: messageID,
    sequence,
    ...(senderUserID === undefined ? {} : { sender_user_id: senderUserID }),
    ...(bodyPreview === undefined ? {} : { body_preview: bodyPreview }),
    ...(typeof hasAttachmentsValue === "boolean" ? { has_attachments: hasAttachmentsValue } : {}),
    ...(createdAt === undefined ? {} : { created_at: createdAt }),
  };
}

function normalizeRealtimeAttachments(value: unknown): ChatAttachment[] | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (!Array.isArray(value)) {
    return null;
  }

  const attachments: ChatAttachment[] = [];
  for (const item of value) {
    if (item === null || typeof item !== "object" || Array.isArray(item)) {
      return null;
    }
    const record = item as Record<string, unknown>;
    if (!hasOnlyFields(record, ATTACHMENT_FIELDS)) {
      return null;
    }
    const mediaID = optionalTrimmedString(record, "media_id");
    const kind = optionalTrimmedString(record, "kind");
    if (mediaID === null || mediaID === undefined || kind === null || kind === undefined) {
      return null;
    }
    attachments.push({
      media_id: mediaID,
      kind: kind.toLowerCase(),
    });
  }

  return attachments.length === 0 ? undefined : attachments;
}

function toLifecycleRealtimeEvent(event: Record<string, unknown>): RealtimeEvent | null {
  if (!hasOnlyLifecycleFields(event)) {
    return null;
  }
  const chatID = event.chat_id;
  const conversationType = event.conversation_type;
  const actorUserID = event.actor_user_id;
  const occurredAt = event.occurred_at;
  if (!isNonEmptyString(chatID)) {
    return null;
  }
  if (!isLifecycleConversationType(conversationType)) {
    return null;
  }
  if (!isNonEmptyString(actorUserID)) {
    return null;
  }
  if (!isValidTimestamp(occurredAt)) {
    return null;
  }

  const expectedState = event.type === "conversation.closed" ? "closed" : "removed";
  if (event.lifecycle_state !== expectedState) {
    return null;
  }

  return {
    type: event.type as string,
    chat_id: chatID.trim(),
    conversation_type: conversationType,
    lifecycle_state: expectedState,
    actor_user_id: actorUserID.trim(),
    occurred_at: occurredAt.trim(),
  };
}

function toRealtimeEvent(payload: unknown): RealtimeEvent | null {
  if (payload === null || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }

  const event = payload as Record<string, unknown>;
  if (typeof event.type !== "string") {
    return null;
  }
  if (typeof event.chat_id !== "string") {
    return null;
  }

  if (event.type === "conversation.closed" || event.type === "conversation.removed") {
    return toLifecycleRealtimeEvent(event);
  }

  if (event.type === "receipt.updated") {
    if (!hasOnlyFields(event, RECEIPT_EVENT_FIELDS)) {
      return null;
    }
    const chatID = optionalTrimmedString(event, "chat_id");
    const receiptUserID = optionalTrimmedString(event, "receipt_user_id");
    const deliveredSeq = optionalNumber(event, "delivered_seq");
    const readSeq = optionalNumber(event, "read_seq");
    const updatedAt = optionalTrimmedString(event, "updated_at");
    if (
      chatID === null ||
      chatID === undefined ||
      receiptUserID === null ||
      receiptUserID === undefined ||
      deliveredSeq === null ||
      deliveredSeq === undefined ||
      readSeq === null ||
      readSeq === undefined ||
      updatedAt === null
    ) {
      return null;
    }
    return {
      type: "receipt.updated",
      chat_id: chatID,
      receipt_user_id: receiptUserID,
      delivered_seq: deliveredSeq,
      read_seq: readSeq,
      ...(updatedAt === undefined ? {} : { updated_at: updatedAt }),
    };
  }

  if (event.type === "message.created" || event.type === "message.updated" || event.type === "message.deleted") {
    if (!hasOnlyFields(event, MESSAGE_EVENT_FIELDS)) {
      return null;
    }
    const chatID = optionalTrimmedString(event, "chat_id");
    const messageID = optionalTrimmedString(event, "message_id");
    const senderUserID = optionalTrimmedString(event, "sender_user_id");
    const idempotencyKey = optionalTrimmedString(event, "idempotency_key");
    const editVersion = optionalNumber(event, "edit_version");
    const rootPostID = optionalTrimmedString(event, "root_post_id");
    const createdAt = optionalTrimmedString(event, "created_at");
    const updatedAt = optionalTrimmedString(event, "updated_at");
    const editedAt = optionalTrimmedString(event, "edited_at");
    const deletedAt = optionalTrimmedString(event, "deleted_at");
    const deletedByUserID = optionalTrimmedString(event, "deleted_by_user_id");
    const sequence = optionalNumber(event, "sequence");
    const replyTo = normalizeRealtimeReference(event.reply_to);
    const forwardedFrom = normalizeRealtimeReference(event.forwarded_from);
    const attachments = normalizeRealtimeAttachments(event.attachments);
    if (
      chatID === null ||
      chatID === undefined ||
      messageID === null ||
      messageID === undefined ||
      senderUserID === null ||
      idempotencyKey === null ||
      editVersion === null ||
      rootPostID === null ||
      createdAt === null ||
      updatedAt === null ||
      editedAt === null ||
      deletedAt === null ||
      deletedByUserID === null ||
      sequence === null ||
      sequence === undefined ||
      sequence <= 0 ||
      replyTo === null ||
      forwardedFrom === null ||
      attachments === null
    ) {
      return null;
    }
    const body = typeof event.body === "string" ? event.body : event.body === undefined ? undefined : null;
    if (body === null) {
      return null;
    }
    return {
      type: event.type,
      chat_id: chatID,
      message_id: messageID,
      sequence,
      ...(senderUserID === undefined ? {} : { sender_user_id: senderUserID }),
      ...(body === undefined ? {} : { body }),
      ...(idempotencyKey === undefined ? {} : { idempotency_key: idempotencyKey }),
      ...(editVersion === undefined ? {} : { edit_version: editVersion }),
      ...(rootPostID === undefined ? {} : { root_post_id: rootPostID }),
      ...(replyTo === undefined ? {} : { reply_to: replyTo }),
      ...(forwardedFrom === undefined ? {} : { forwarded_from: forwardedFrom }),
      ...(attachments === undefined ? {} : { attachments }),
      ...(createdAt === undefined ? {} : { created_at: createdAt }),
      ...(updatedAt === undefined ? {} : { updated_at: updatedAt }),
      ...(editedAt === undefined ? {} : { edited_at: editedAt }),
      ...(deletedAt === undefined ? {} : { deleted_at: deletedAt }),
      ...(deletedByUserID === undefined ? {} : { deleted_by_user_id: deletedByUserID }),
    };
  }

  return null;
}

export function connectRealtime(options: RealtimeClientOptions): RealtimeSubscription {
  const origin = resolveGatewayOrigin(options.gatewayOrigin);
  const url = resolveRealtimeURL(origin, options.accessToken);
  let socket: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectAttempts = 0;
  let stopped = false;
  let closeRequestedWhileConnecting = false;

  const clearReconnectTimer = () => {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (stopped || reconnectTimer !== null) {
      return;
    }
    const delay = Math.min(
      REALTIME_RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempts,
      REALTIME_RECONNECT_MAX_DELAY_MS,
    );
    reconnectAttempts += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  };

  const connect = () => {
    if (stopped) {
      return;
    }

    closeRequestedWhileConnecting = false;
    const activeSocket = new WebSocket(url);
    socket = activeSocket;

    activeSocket.addEventListener("open", () => {
      if (socket !== activeSocket) {
        return;
      }
      if (closeRequestedWhileConnecting || stopped) {
        activeSocket.close();
        return;
      }
      reconnectAttempts = 0;
      options.onOpen?.();
    });

    activeSocket.addEventListener("close", (event) => {
      if (socket === activeSocket) {
        socket = null;
      }
      options.onClose?.(event);
      if (stopped) {
        return;
      }
      scheduleReconnect();
    });

    activeSocket.addEventListener("error", (event) => {
      options.onError?.(event);
    });

    activeSocket.addEventListener("message", (event) => {
      const data = event.data;
      if (typeof data !== "string") {
        return;
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }

      const realtimeEvent = toRealtimeEvent(parsed);
      if (realtimeEvent !== null) {
        options.onEvent(realtimeEvent);
      }
    });
  };

  connect();

  return {
    close: () => {
      stopped = true;
      clearReconnectTimer();
      if (socket === null) {
        return;
      }
      if (socket.readyState === WebSocket.CONNECTING) {
        closeRequestedWhileConnecting = true;
        return;
      }
      if (socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
    },
  };
}
